// Manifesto declarativo: pra cada gateway, lista quais endpoints da nossa API
// chamam o provider externo e quais webhooks recebemos de volta. Cada endpoint
// tem path + método + payload de exemplo (request + response). Usado pelo
// drill-down em PaymentsConfigView ao clicar no card do gateway.
//
// IMPORTANTE: esse arquivo é a FONTE DE VERDADE da documentação visível pelo
// admin. Quando portarmos novo método/integração, ATUALIZAR aqui. Evita
// drift entre código e docs.

export type ProviderId = "mock" | "stripe" | "mercadopago" | "celcoin";

export interface ApiCallDoc {
  /** Nome curto humano-legível (ex: "Cobrança cartão", "Criar Pix") */
  label: string;
  /** Quando este endpoint é chamado: cron mensal, ação do aluno, etc */
  when: string;
  /** Método HTTP + path externo */
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  externalPath: string;
  /** Nossa função que dispara (file:func) — facilita pra dev pular pro código */
  ourCallsite: string;
  /** Status: implementado / esqueleto / não suportado / em validação */
  status: "implemented" | "skeleton" | "validating" | "not_supported";
  requestExample?: object;
  responseExample?: object;
  notes?: string;
}

export interface WebhookDoc {
  /** Nome do evento (ex: PIX.PAID, CHARGE.AUTHORIZED) */
  event: string;
  /** O que fazemos quando esse evento chega */
  effect: string;
  status: "implemented" | "skeleton" | "validating" | "not_supported";
  payloadExample?: object;
}

export interface ProviderDoc {
  id: ProviderId;
  label: string;
  /** URL da documentação oficial (abre em nova aba) */
  docsUrl?: string;
  /** Base URL sandbox + prod */
  sandboxBaseUrl?: string;
  prodBaseUrl?: string;
  /** Como autenticamos: oauth_client_credentials | api_key | none */
  auth: "oauth_client_credentials" | "api_key" | "none";
  authNotes?: string;
  /** Quais APIs chamamos */
  apiCalls: ApiCallDoc[];
  /** Quais webhooks escutamos */
  webhooks: WebhookDoc[];
}

export const PROVIDER_DOCS: Record<ProviderId, ProviderDoc> = {
  mock: {
    id: "mock",
    label: "Mock",
    auth: "none",
    apiCalls: [
      {
        label: "Tokenizar cartão",
        when: "Quando aluno adiciona cartão em Forma de Pagamento",
        method: "POST",
        externalPath: "(local — sem chamada externa)",
        ourCallsite: "apps/api/src/payments/gateway.ts:tokenizeCardMock",
        status: "implemented",
        requestExample: { number: "4111 1111 1111 1111", holderName: "Test User" },
        responseExample: {
          token: "mock_card_1738294920_xyz123",
          brand: "Visa",
          last4: "1111"
        }
      },
      {
        label: "Cobrar cartão",
        when: "Cron mensal (subscription) + pagamento avulso (pacote)",
        method: "POST",
        externalPath: "(local — sem chamada externa)",
        ourCallsite: "apps/api/src/payments/gateway.ts:chargeMock",
        status: "implemented",
        responseExample: { ok: true, gatewayChargeId: "mock_sub-abc-2026-05-18-p0" },
        notes:
          "mockAlwaysSucceeds=true → sempre passa. false → 30% falha aleatória (testa fluxo cartão reserva)."
      }
    ],
    webhooks: [
      {
        event: "(nenhum)",
        effect:
          "Mock confirma cobrança sincronamente. Não precisa webhook — `chargeMock` já volta status final.",
        status: "not_supported"
      }
    ]
  },

  celcoin: {
    id: "celcoin",
    label: "Celcoin (cel_cash)",
    docsUrl: "https://docs-celpayments.celcoin.com.br/",
    sandboxBaseUrl: "https://sandbox.openfinance.celcoin.dev/v5",
    prodBaseUrl: "https://openfinance.celcoin.com.br/v5",
    auth: "oauth_client_credentials",
    authNotes:
      "POST /token com client_id + client_secret. Token TTL ~60min, cacheamos em memória. clientSecret no DB vai cifrado (AES-256-GCM).",
    apiCalls: [
      {
        label: "Autenticar (gerar token)",
        when: "Primeira chamada e a cada ~55 min (TTL do token)",
        method: "POST",
        externalPath: "/token",
        ourCallsite: "apps/api/src/payments/celcoin.ts:getAccessToken",
        status: "validating",
        requestExample: {
          grant_type: "client_credentials",
          client_id: "cel_xxxxxxxxxx",
          client_secret: "(cifrado no DB)"
        },
        responseExample: {
          access_token: "eyJhbGc...",
          token_type: "Bearer",
          expires_in: 3600
        },
        notes:
          "TODO(celcoin-docs): confirmar path exato + formato do body (form-urlencoded vs json)."
      },
      {
        label: "Criar cobrança Pix avulsa (dinâmica)",
        when:
          "Cron mensal quando pixProvider='celcoin'. NUNCA usamos Pix recorrente nativo — cada mês é uma cobrança independente.",
        method: "POST",
        externalPath: "/pix/charges",
        ourCallsite: "apps/api/src/payments/celcoin.ts:createCelcoinPix",
        status: "validating",
        requestExample: {
          amount: 19.9,
          clientRequestId: "sub-abc12345-2026-05",
          description: "Plano Pro · luis.moraes",
          debtor: { document: "12345678900", name: "Luis Moraes" },
          expiration: 259200
        },
        responseExample: {
          transactionId: "celcoin_tx_abc123",
          emvqrcps:
            "00020126360014BR.GOV.BCB.PIX0114+551112345678952040000530398654041.9905802BR5910CELCOIN6008SAO PAULO62070503***6304ABCD",
          qrCodeBase64: "iVBORw0KGgo... (PNG opcional)"
        },
        notes:
          "TODO(celcoin-docs): confirmar campo `emvqrcps` vs `brcode`/`pixCopyPaste`. Também valor (centavos vs reais)."
      },
      {
        label: "Cobrar cartão tokenizado (one-shot)",
        when: "Cron mensal quando ccProvider='celcoin' + cobrança avulsa",
        method: "POST",
        externalPath: "/charges/cards",
        ourCallsite: "apps/api/src/payments/celcoin.ts:chargeCelcoinCard",
        status: "validating",
        requestExample: {
          amount: 1990,
          description: "Plano Pro",
          cardToken: "tok_xxxxxxx",
          capture: true,
          billing: {
            zipCode: "01310100",
            street: "Av Paulista",
            number: "1000",
            city: "São Paulo",
            state: "SP"
          }
        },
        responseExample: {
          id: "celcoin_chg_xyz",
          status: "APPROVED",
          authorizationCode: "123456"
        },
        notes:
          "TODO(celcoin-docs): confirmar path + status enums (APPROVED|CAPTURED|DENIED). Recorrência fica com nosso cron — cada charge é avulsa."
      }
    ],
    webhooks: [
      {
        event: "PIX.RECEIVED",
        effect:
          "Marca subscription_charges.status=succeeded pelo clientRequestId (pixTxid). Idempotente por eventId via provider_webhook_events. Recebido em POST /api/webhooks/celcoin.",
        status: "validating",
        payloadExample: {
          event: "PIX.RECEIVED",
          eventId: "evt_123",
          occurredAt: "2026-05-18T14:32:11Z",
          data: {
            transactionId: "celcoin_tx_abc123",
            clientRequestId: "sub-abc12345-2026-05",
            amount: 19.9,
            payer: { document: "12345678900", name: "Luis Moraes" }
          }
        }
      },
      {
        event: "PIX.EXPIRED",
        effect:
          "Marca subscription_charges.status=failed + failureReason='celcoin:PIX.EXPIRED'. O cron-reconcile-charges também faz fallback se o webhook não chegar (TTL 1h).",
        status: "validating"
      },
      {
        event: "CHARGE.AUTHORIZED / CHARGE.CAPTURED",
        effect:
          "Confirma sub_charge succeeded pelo gatewayChargeId. Mesma idempotência por eventId.",
        status: "validating"
      },
      {
        event: "CHARGE.DENIED",
        effect:
          "Marca failed + failureReason='celcoin:CHARGE.DENIED'. Cron diário tenta cartão reserva na próxima janela.",
        status: "validating"
      },
      {
        event: "CHARGE.REFUNDED",
        effect:
          "Marca status=refunded. Não reverte sub (admin precisa cancelar manualmente se for o caso).",
        status: "validating"
      }
    ]
  },

  stripe: {
    id: "stripe",
    label: "Stripe",
    docsUrl: "https://docs.stripe.com/",
    sandboxBaseUrl: "(test mode — mesma base URL com chaves sk_test_*)",
    prodBaseUrl: "https://api.stripe.com",
    auth: "api_key",
    authNotes: "Bearer com secret key (sk_live_* / sk_test_*).",
    apiCalls: [
      {
        label: "Tokenizar cartão (client-side)",
        when: "No browser do aluno via Stripe.js Elements — backend nunca vê PAN",
        method: "POST",
        externalPath: "/v1/payment_methods (client SDK)",
        ourCallsite: "(não implementado)",
        status: "not_supported"
      },
      {
        label: "Criar PaymentIntent + confirmar",
        when: "Cron mensal / cobrança avulsa",
        method: "POST",
        externalPath: "/v1/payment_intents",
        ourCallsite: "apps/api/src/payments/gateway.ts:chargeWithGateway (case stripe)",
        status: "not_supported",
        notes: "Hoje retorna `not_implemented`. Precisa portar quando cliente decidir Stripe."
      }
    ],
    webhooks: [
      {
        event: "payment_intent.succeeded",
        effect: "(não implementado)",
        status: "not_supported"
      },
      {
        event: "payment_intent.payment_failed",
        effect: "(não implementado)",
        status: "not_supported"
      }
    ]
  },

  mercadopago: {
    id: "mercadopago",
    label: "Mercado Pago",
    docsUrl: "https://www.mercadopago.com.br/developers/pt",
    sandboxBaseUrl: "(test users)",
    prodBaseUrl: "https://api.mercadopago.com",
    auth: "api_key",
    authNotes: "Bearer com access token.",
    apiCalls: [
      {
        label: "Criar payment",
        when: "Cobrança avulsa CC + Pix",
        method: "POST",
        externalPath: "/v1/payments",
        ourCallsite: "apps/api/src/payments/gateway.ts:chargeWithGateway (case mercadopago)",
        status: "not_supported"
      }
    ],
    webhooks: [
      {
        event: "payment.created / payment.updated",
        effect: "(não implementado)",
        status: "not_supported"
      }
    ]
  }
};
